import React from 'react';
import { buildSimplePrintDocument } from './simple-document';
import type { PrintTemplateId } from '../page-template';
import type { PrintColumn, PrintDocumentModel } from '../types';
import type { SimplePrintLine } from './simple-document';
/**
 * Generic A4 commercial / voucher / QMS / report adapter.
 * Use for documents that are primarily a title + meta + line table + totals.
 */
export function buildCommercialA4PrintDocument(input: {
  docType: string;
  title: string;
  documentNumber: string;
  partyName: string;
  templateId?: PrintTemplateId;
  /** When true, use shared company letterhead header/footer artwork. */
  letterhead?: boolean;
  metaLines?: Array<{ label: string; value: string }>;
  introText?: string;
  columns: PrintColumn[];
  lines: SimplePrintLine[];
  finals?: Array<{ id: string; heightPx: number; keepTogetherWithNext?: boolean }>;
  finalBlocks?: Record<string, React.ReactNode>;
  landscape?: boolean;
}): PrintDocumentModel {
  const useLh = input.letterhead === true;
  const templateId: PrintTemplateId = input.landscape
    ? 'a4-landscape'
    : input.templateId ?? (useLh ? 'letter-letterhead' : 'a4-portrait');

  return buildSimplePrintDocument({
    docType: input.docType,
    title: input.title,
    documentNumber: input.documentNumber,
    partyName: input.partyName,
    templateId,
    letterhead: useLh ? undefined : false,
    metaLines: input.metaLines,
    introText: input.introText,
    columns: input.columns,
    lines: input.lines,
    finals: input.finals,
    renderPayload: {
      metaLines: input.metaLines,
      introText: input.introText,
      finalBlocks: input.finalBlocks,
    },
  });
}

export function buildReportPrintDocument(input: {
  docType: string;
  title: string;
  documentNumber: string;
  partyName?: string;
  metaLines?: Array<{ label: string; value: string }>;
  columns: PrintColumn[];
  lines: SimplePrintLine[];
  finalNote?: string;
  /** Wide registers (invoice detail, aging, etc.) — A4 landscape report profile. */
  landscape?: boolean;
}): PrintDocumentModel {
  return buildSimplePrintDocument({
    docType: input.docType,
    title: input.title,
    documentNumber: input.documentNumber,
    partyName: input.partyName || '',
    templateId: input.landscape ? 'a4-report-landscape' : 'a4-report',
    letterhead: false,
    metaLines: input.metaLines,
    columns: input.columns,
    lines: input.lines,
    finals: input.finalNote ? [{ id: 'note', heightPx: 40 }] : [],
    renderPayload: {
      metaLines: input.metaLines,
      finalBlocks: input.finalNote
        ? { note: <p>{input.finalNote}</p> }
        : {},
    },
  });
}
